import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Drawer, Grid, Button, Divider, useMediaQuery } from '@material-ui/core';
import KeyboardArrowLeft from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight';
import classnames from 'classnames';
import CloseIcon from 'assets/images/close-icon.svg';
import { hasPermissionsFor, checkUserRole } from 'helpers/_helpers';
import { shortDate, isWithinCurrentWeek } from 'helpers/_date-helpers';
import { drawerTypes } from 'pages/Equipment/components/helpers/drawerTypes';
import PageLoader from '../PageLoader';
import ConfirmDialog from '../ConfirmDialog';
import CrewBlock from './components/CrewBlock';
import EquipmentBlock from './components/EquipmentBlock';
import SubmitSection from './components/SubmitSection';
import MaterialsBlock from './components/MaterialsBlock';
import CancelModal from './components/CancelModal';
import TimeBlock from './components/TimeBlock';
import WeatherBlock from './components/WeatherBlock';
import NotesBlock from './components/NotesBlock';
import GeneralInfoBlock from './components/GeneralInfoBlock';
import OverlappingModal from '../OverlapsingModal';
import ChangeTimeModal from '../OverlapsingModal/ChangeTimeModal';
import {
  updateSheetAddResource,
  getWorkers,
  getEquipment,
} from 'store/schedule/schedulePageOperation';
import {
  fetchWorklogMaterials,
  deleteAllWorklogMaterials,
  deleteWorklogAllResources,
  getTimeoffs,
  patchCancelWorklog,
  patchUncancelWorklog,
} from 'store/currentWorklog/operations';

import styles from './styles';
import { generateHoursObject, getEffectiveStartTime } from 'helpers/helpers';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { fetchWorklogSheet } from 'store/currentWorklog/operations';
import currentWorklogActions from 'store/currentWorklog/actions';
import { userWorklogsDayHours } from './hooks/apiCalls';
import AlertModal from 'components/AlertModal/AlertModal';
import { fetchUsers } from 'store/people/peoplePageOperations';
import { useDuplicate } from './hooks/useDuplicate';
import { useWeekStart } from 'hooks/useWeekStart';
import {
  SHEET_MATERIALS_EVENT,
  SHEET_SURFACES_EVENT,
  TIMEOFFS_EVENT,
  SHEET_EVENT,
  WORKER_EVENT,
  CONTRACTORS_EVENT,
  PROJECTS_EVENT,
  EQUIPMENT_EVENT,
  SETTINGS_EVENT,
} from 'subscriptions/events/events.enum';
import personalProfileActions from 'store/personalProfile/personalProfileActions';
import { useTimeSettingsFormat } from 'common/useTimeSettingsFormat';
import { useCanEditWorklog, useCanEditWorklogCrewAndTime } from 'common/hooks/use-can-edit-worklog';
import { getDwlUsers } from 'store/settings/settingsOperations';
import { useSearchParams } from '../../../node_modules/react-router-dom/index';
import SubmittedConfirmModal from './components/SubmittedConfirmModal/SubmittedConfirmModal';
import HistoryDrawer from './components/HistoryDrawer/HistoryDrawer';
import { WORKLOG_DRAWER_TYPES, PAGE_CONTEXTS } from './constants';
import { useRemoveSheet } from 'pages/ScheduleV6/components/hooks/useRemoveSheet';
import { useBootstrapLaborClassifications } from 'store/laborClassifications';
import RecentChanges from './components/RecentChanges';
import { fetchRecentChanges, recentChangesCleared } from 'store/recentChanges';


const WorklogDetails = ({
  openSnackbar,
  openAdditionalDrawer,
  loading,
  shouldNotRender = false,
  hasMore = false,
  sheetIds = [],
  openDrawer,
  pageCount = 0,
  updateDrawerFromRouter,
  updateVisibleSheets,
  showIndividualStartTime,
  noAccessCallback,
  mobileViewSheetsRefresh,
  getFullRefreshedList,
  pageContext,
  showRecentChanges = false,
}) => {
  const isSchedule =
    pageContext === PAGE_CONTEXTS.schedule || pageContext === PAGE_CONTEXTS.workerSchedule;
  const isTimeClock = pageContext === PAGE_CONTEXTS.timeClock;

  const useStyles = makeStyles(styles);
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const params = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const user = useSelector((store) => store.personalProfile.user) || {};
  const sheet = useSelector((store) => store.currentWorklog.worklogSheet.sheet);
  const historyState = useSelector((store) => store.currentWorklog.worklogSheet.historyState);
  const submittedChangesModal = useSelector(store => store.currentWorklog.submittedChangesModal);
  const getTimeFormatHook = useTimeSettingsFormat();

  useBootstrapLaborClassifications();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [overlappingModal, setOverlappingModal] = useState(null);
  const [userWorklogsModal, setUserWorklogsModal] = useState(null);
  const [equipWorklogsModal, setEquipWorklogsModal] = useState(null);
  const [localShouldNotRender, setLocalShouldNotRender] = useState(shouldNotRender);
  const [infoModal, setInfoModal] = useState({
    isOpen: false,
    text: '',
  });
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    text: 'Are you sure you want to clear all resources? This action cannot be reversed.',
    type: 'resources',
  });
  const [historyDrawer, setHistoryDrawer] = useState({
    isOpen: false,
  })

  const dispatch = useDispatch();

  const { DuplicateModalComponent, setDuplicateModal } = useDuplicate(sheet._id, openSnackbar);
  const { removeSheet } = useRemoveSheet({ sheetId: sheet._id });

  const worklogsHistoryStates = useMemo(() => {
    return (sheet?.submissionStates || []).map(submissionState => {
      const wlState = JSON.parse(submissionState.sheetState);
      wlState.createdAt = new Date(wlState.createdAt);
      wlState.createdAtDate = new Date(wlState.createdAtDate);
      wlState.hours = {
        ...wlState.hours,
        start: new Date(wlState.hours?.start),
        end: new Date(wlState.hours?.end),
      };
      wlState.images = (wlState.images || []).map((image) => ({ ...image, createdAt: new Date(image.createdAt) }));
      wlState.workers = (wlState.workers || []).map((worker) => ({
        ...worker,
        hours: worker.hours?.map(hours => ({
          ...hours,
          start: hours?.start ? new Date(hours.start) : undefined,
          end: hours?.end ? new Date(hours.end) : undefined,
        }))
      }));
      wlState.equipment = (wlState.equipment || []).map((equip) => ({
        ...equip,
        hours: equip.hours?.map(hours => ({
          ...hours,
          start: hours?.start ? new Date(hours.start) : undefined,
          end: hours?.end ? new Date(hours.end) : undefined,
        }))
      }));

      return {
        ...submissionState,
        sheetState: wlState,
        diff: (!!submissionState.diff && submissionState.diff !== 'no diff')
          ? JSON.parse(submissionState?.diff)
          : undefined,
      }
    })
  }, [sheet?.submissionStates]);

  const displaySheetState = useMemo(() => historyState?.sheetState || sheet, [sheet, historyState])

  const searchParamsSheetId = searchParams.get('sheetId') || params.sheetId;

  useEffect(() => {
    setHistoryDrawer({ isOpen: false });
    if (searchParamsSheetId) {
      dispatch(fetchWorklogMaterials(searchParamsSheetId));
      dispatch(fetchWorklogSheet(searchParamsSheetId));
      dispatch(getWorkers());
      dispatch(getEquipment());
      dispatch(fetchUsers({}, 0, Infinity));
      dispatch(getTimeoffs(searchParamsSheetId));
      dispatch(getDwlUsers());
      if (showRecentChanges) dispatch(fetchRecentChanges(searchParamsSheetId));

      // sockets
      dispatch(currentWorklogActions.socketConnect(SHEET_MATERIALS_EVENT));
      dispatch(currentWorklogActions.socketConnect(SHEET_SURFACES_EVENT));
      dispatch(currentWorklogActions.socketConnect(TIMEOFFS_EVENT));
      dispatch(currentWorklogActions.socketConnect(SHEET_EVENT));
      dispatch(currentWorklogActions.socketConnect(WORKER_EVENT));
      dispatch(currentWorklogActions.socketConnect(CONTRACTORS_EVENT));
      dispatch(currentWorklogActions.socketConnect(PROJECTS_EVENT));
      dispatch(currentWorklogActions.socketConnect(EQUIPMENT_EVENT));
      dispatch(personalProfileActions.socketConnect(SETTINGS_EVENT));
      return () => {
        dispatch(currentWorklogActions.socketDisconnect(SHEET_MATERIALS_EVENT));
        dispatch(currentWorklogActions.socketDisconnect(SHEET_SURFACES_EVENT));
        dispatch(currentWorklogActions.socketDisconnect(TIMEOFFS_EVENT));
        dispatch(currentWorklogActions.socketDisconnect(SHEET_EVENT));
        dispatch(currentWorklogActions.socketDisconnect(WORKER_EVENT));
        dispatch(currentWorklogActions.socketDisconnect(CONTRACTORS_EVENT));
        dispatch(currentWorklogActions.socketDisconnect(PROJECTS_EVENT));
        dispatch(currentWorklogActions.socketDisconnect(EQUIPMENT_EVENT));
        dispatch(personalProfileActions.socketDisconnect(SETTINGS_EVENT));
        dispatch(currentWorklogActions.clearHistoryState());
        if (showRecentChanges) dispatch(recentChangesCleared());
      };
    }
  }, [searchParamsSheetId]);

  useEffect(() => {
    if (!shouldNotRender) {
      updateDrawerFromRouter();
    }
  }, []);

  useEffect(() => {
    if (localShouldNotRender !== shouldNotRender) {
      setLocalShouldNotRender(shouldNotRender);
      updateDrawerFromRouter();
    }
  }, [shouldNotRender]);

  const closeInfoModal = () => setInfoModal({ isOpen: false, text: '' });


  const isFirstInList = useMemo(() => {
    return sheetIds?.findIndex((id) => id === sheet?._id) === 0;
  }, [sheetIds, sheet]);

  const isLastInList = useMemo(() => {
    return sheetIds?.findIndex((id) => id === sheet?._id) === sheetIds?.length - 1;
  }, [sheetIds, sheet]);


  // useEffect(() => {
  //   if (sheetIds?.length && sheet && isLastInList && hasMore && !loading) {
  //     updateVisibleSheets(false, pageCount + 1)
  //   }
  // }, [sheetIds, sheet, isLastInList, hasMore, updateVisibleSheets, loading]);


  // TODO: review this change and make it better if possible.
  // Case - closing my schedule worklog details on deleting worker (field tech) from resources
  const hasAccess = useCanEditWorklog(sheet) && !historyDrawer?.isOpen && !historyState?._id;
  const canEditCrewAndTime = useCanEditWorklogCrewAndTime(sheet) && !historyDrawer?.isOpen && !historyState?._id;

  // to close Worklog Details on my-schedule/full-schedule (field-tech)
  useEffect(() => {
    if (
      loading ||
      !sheet?._id ||
      sheet?._id !== searchParamsSheetId ||
      !user?._id ||
      !noAccessCallback ||
      typeof noAccessCallback !== 'function'
    )
      return;

    if (!hasAccess) noAccessCallback();
  }, [searchParamsSheetId, loading, sheet, user, noAccessCallback, hasAccess]);


  if (!searchParamsSheetId || !sheet || !Object.keys(sheet).length) {
    return null;
  }

  const close = () => {
    if ((!isSchedule || isTimeClock) && getFullRefreshedList) {
      getFullRefreshedList(sheet);
    }

    const arrPath = location.pathname.split('/').filter(p => !!p);
    // if not a schedule
    if (!/\/\d{4}-\d{2}-\d{2}$/i.test(location.pathname)) arrPath.pop()
    dispatch(currentWorklogActions.clearWorklogSheet());
    navigate('/' + arrPath.filter(p => !!p).join('/'), { replace: true });
  };

  const handleClose = () => {
    if (submittedChangesModal.isWlChanged) {
      dispatch(currentWorklogActions.openSubmittedChangesModal({ onFinish: close }));
    } else {
      close();
    }
  }

  const closeOverlappingModal = () => {
    setOverlappingModal(null);
  };

  const getFormattedHoursRange = (userHours) => {
    const { createdAt, hours } = sheet;
    const hoursToUse = userHours || hours;

    if (!hoursToUse) return;

    const { hourStart, minuteStart, amPmStart, dayStart, hourEnd, minuteEnd, amPmEnd, dayEnd } =
      generateHoursObject(hoursToUse.start, hoursToUse.end, createdAt, getTimeFormatHook);

    const startObj = [null, hourStart, minuteStart, amPmStart];
    const endObj = [null, hourEnd, minuteEnd, amPmEnd];
    const start = `${getTimeFormatHook(startObj).formattedTime} ${shortDate(
      createdAt,
      dayStart,
      getTimeFormatHook().localDayMonth
    )}`;
    const end = `${getTimeFormatHook(endObj).formattedTime} ${shortDate(
      createdAt,
      dayEnd,
      getTimeFormatHook().localDayMonth
    )}`;

    return `${start} \u2013 ${end}`;
  };

  const clearAllResources = () => {
    dispatch(deleteAllWorklogMaterials());
    dispatch(updateSheetAddResource(sheet._id, 1));
    dispatch(deleteWorklogAllResources(sheet._id));
    closeModal();
    if (sheet.submittedAt) {
      dispatch(currentWorklogActions.submittedChangesModalWlChanged());
    }
  };

  const clearAllResourcesModal = () => {
    setConfirmModal((prev) => ({ ...prev, isOpen: true }));
  };

  const goToPrevSheet = () => {
    const currentIndex = sheetIds.findIndex((el) => el === sheet._id);
    const arrPath = location.pathname.split('/').filter(p => !!p);
    arrPath.pop();
    arrPath.push(sheetIds[currentIndex - 1]);
    navigate('/' + arrPath.filter(p => !!p).join('/'), { replace: true });
  };

  const goToNextSheet = () => {
    const currentIndex = sheetIds.findIndex((el) => el === sheet._id);
    const arrPath = location.pathname.split('/').filter(p => !!p);
    arrPath.pop();
    arrPath.push(sheetIds[currentIndex + 1]);
    navigate('/' + arrPath.filter(p => !!p).join('/'), { replace: true });
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setIsDeleteModalOpen(false);
    setIsCancelModalOpen(false);
    setConfirmModal((prev) => ({ ...prev, isOpen: false }));
  };

  const submitModal = () => {
    setIsModalOpen(false);
    openDrawer(WORKLOG_DRAWER_TYPES.update, sheet);
  };

  const getScheduleFuncText = () => {
    const { published, canceledAt } = sheet;
    if (!published) return 'Remove from schedule';
    return canceledAt ? 'Mark as not canceled' : 'Mark as canceled';
  };

  const runCorrespondAction = async () => {
    const { published, canceledAt, _id } = sheet;
    if (!published) return setIsDeleteModalOpen(true);
    if (!canceledAt) return setIsCancelModalOpen(true);

    try {
      await dispatch(patchUncancelWorklog(sheet._id));
      await dispatch(updateSheetAddResource(sheet._id, 1));
      if (sheet.submittedAt) {
        dispatch(currentWorklogActions.submittedChangesModalWlChanged());
      }
    } catch (e) {
      console.log(e);
      if (e.response && e.response.data.type === 'sheet-time-overlapse') {
        setOverlappingModal({
          reason: e.response.data.message,
          data: e.response.data.data,
        });
      }
      if (e.response && e.response.data.type === 'timeoff-time-overlapse') {
        setInfoModal({ isOpen: true, text: e.response.data.message });
      }
    }
  };

  const handleDeleteSheet = () => {
    removeSheet().then(() => {
      mobileViewSheetsRefresh && mobileViewSheetsRefresh();
      setIsDeleteModalOpen(false);
    });
  };

  const handleFailedUncancel = () => {
    closeOverlappingModal();
    runCorrespondAction();
  };

  const cancelSheet = async (text) => {
    try {
      closeModal();
      await dispatch(patchCancelWorklog(sheet._id, { cancelNote: text }));
      dispatch(updateSheetAddResource(sheet._id, 1));
      if (sheet.submittedAt) {
        dispatch(currentWorklogActions.submittedChangesModalWlChanged());
      }
    } catch (e) {
      console.log(e);
    }
  };

  const hasSubmitAccess = () => {
    const userId = user._id;
    const isAssignedWorker = sheet.workers.some((worker) => worker._id === userId);
    const isFieldTechnician = checkUserRole('Field Technician');

    if (isFieldTechnician && !isAssignedWorker) {
      return Boolean(sheet.submissions && sheet.submissions.length);
    }
    return Boolean((sheet.submissions && sheet.submissions.length) || hasAccess);
  };

  const openUserWorklogsModal = (workerId, workerHours, worker) => async () => {
    const res = await userWorklogsDayHours(sheet._id, workerId);
    setUserWorklogsModal({ ...res, extendedWorker: worker });
  };

  const closeUserWorklogsModal = () => {
    setUserWorklogsModal(null);
  };

  const openEquipWorklogsModal = (equipId, workerHours, equipment) => async () => {
    const res = await userWorklogsDayHours(sheet._id, equipId);
    setEquipWorklogsModal({ ...res, extendedWorker: equipment });
  };


  const getStartTime = () => {
    if (showIndividualStartTime) {
      const workerId = user._id;
      const worker = sheet.workers.find(({ _id }) => _id === workerId);
      if (worker && worker.startTime) {
        return getEffectiveStartTime(worker.startTime, worker.startTimeDate);
      }
    }
    return getEffectiveStartTime(sheet.startTime, sheet.startTimeDate);
  };

  const onHistoryDrawerOpen = () => {
    if (historyDrawer?.isOpen) {
      setHistoryDrawer({ isOpen: false });
      dispatch(currentWorklogActions.clearHistoryState());
    } else {
      setHistoryDrawer({ isOpen: true });
    }
  }

  const onHistoryDrawerClose = () => {
    dispatch(currentWorklogActions.clearHistoryState());
    setHistoryDrawer({ isOpen: false });
  }

  const onHistorySubmissionSelect = (submissionId) => {
    const historyState = worklogsHistoryStates.find(historyState => historyState.submissionId === submissionId);
    if (!historyState) return;

    dispatch(currentWorklogActions.setHistoryState(historyState));
    if (isMobile) setHistoryDrawer({ isOpen: false });
  }


  return (
    <>
      <Drawer
        anchor="right"
        open
        PaperProps={{
          classes: {
            root: classes.fixSafariOverflow,
          },
        }}
      >
        <div
          className={classnames(classes.formWrapper, isSchedule && classes.paddingBottom50)}
          role="presentation"
        >
          {loading ? (
            <PageLoader loading>
              <div />
            </PageLoader>
          ) : (
            <Grid container spacing={2}>
              {!isSchedule && !isFirstInList && (
                <div className={classnames(classes.nav, classes.navLeft)} onClick={goToPrevSheet}>
                  <KeyboardArrowLeft className={classes.navIcon} />
                </div>
              )}
              {!isSchedule && !isLastInList && (
                <div className={classnames(classes.nav, classes.navRight)} onClick={goToNextSheet}>
                  <KeyboardArrowRight className={classes.navIcon} />
                </div>
              )}
              <DuplicateModalComponent />

              {showRecentChanges && isMobile && (
                <Grid item xs={12}>
                  <RecentChanges sheetId={displaySheetState._id} />
                </Grid>
              )}

              <GeneralInfoBlock
                openAdditionalDrawer={openAdditionalDrawer}
                hasAccess={hasAccess}
                sheet={displaySheetState}
                user={user}
                close={handleClose}
                openSnackbar={openSnackbar}
                setDuplicateModal={setDuplicateModal}
                onHistoryDrawerOpen={onHistoryDrawerOpen}
                historyState={historyState}
                pageContext={pageContext}
              />

              <Grid item xs={12}>
                <Divider />
              </Grid>

              <NotesBlock
                hasAccess={hasAccess}
                notes={displaySheetState.notes}
                noteComments={sheet.noteComments}
                schedulerNotes={displaySheetState.schedulerNotes}
                openAdditionalDrawer={openAdditionalDrawer}
                sheet={displaySheetState}
                historyState={historyState}
              />

              <Grid item xs={12}>
                <Divider />
              </Grid>

              <WeatherBlock
                weather={displaySheetState.weather}
                hasAccess={hasAccess}
                openAdditionalDrawer={openAdditionalDrawer}
                historyState={historyState}
              />

              <Grid item xs={12}>
                <Divider />
              </Grid>

              <TimeBlock
                getFormattedHoursRange={getFormattedHoursRange}
                travelTime={displaySheetState.travelTime}
                openAdditionalDrawer={openAdditionalDrawer}
                hasAccess={hasAccess}
                hours={displaySheetState.hours}
                sheet={displaySheetState}
                currentWorker={displaySheetState.workers?.find(worker => worker._id === user._id)}
                historyState={historyState}
              />

              <Grid item xs={12}>
                <Divider />
              </Grid>

              <Grid item xs={12} md={12}>
                <CrewBlock
                  workers={displaySheetState.workers}
                  defaultLaborClassificationId={displaySheetState.defaultLaborClassificationId}
                  getFormattedHoursRange={getFormattedHoursRange}
                  openAdditionalDrawer={openAdditionalDrawer}
                  openUserWorklogs={openUserWorklogsModal}
                  hasAccess={hasAccess}
                  historyState={historyState}
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <EquipmentBlock
                  equipment={displaySheetState.equipment}
                  getFormattedHoursRange={getFormattedHoursRange}
                  openAdditionalDrawer={openAdditionalDrawer}
                  // openUserWorklogs={openEquipWorklogsModal}
                  hasAccess={hasAccess}
                  historyState={historyState}
                />
              </Grid>
              {Boolean(sheet.workers.length || sheet.equipment.length) &&
                canEditCrewAndTime &&
                !checkUserRole('Field Technician') && (
                  <Grid item xs={12} className={classes.textCenter}>
                    <Button
                      variant="text"
                      className={classes.deleteButton}
                      onClick={clearAllResourcesModal}
                      disableRipple
                    >
                      <img src={CloseIcon} alt="delete" />
                      Clear all resources
                    </Button>
                  </Grid>
                )}

              <Grid item xs={12}>
                <Divider />
              </Grid>
              {sheet.published && hasSubmitAccess() && (
                <>
                  <Grid item xs={12}>
                    <SubmitSection
                      submissions={sheet.submissions || []}
                      sheet={sheet}
                      openSnackbar={openSnackbar}
                      hasAccess={hasAccess}
                      user={user}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                </>
              )}

              <MaterialsBlock
                openAdditionalDrawer={openAdditionalDrawer}
                sheet={displaySheetState}
                openSnackbar={openSnackbar}
                hasAccess={hasAccess}
                historyState={historyState}
              />

              {isSchedule && hasPermissionsFor('worklogsFullAccess') && (
                <div className={classes.bottomPanel}>
                  <Button
                    variant="text"
                    className={classes.scheduleFuncBtn}
                    onClick={runCorrespondAction}
                    disableRipple
                  >
                    {!sheet.published && <img src={CloseIcon} alt="close" />}
                    {getScheduleFuncText()}
                  </Button>
                </div>
              )}
            </Grid>
          )}
        </div>

        <HistoryDrawer
          open={historyDrawer.isOpen}
          onClose={onHistoryDrawerClose}
          submissions={sheet.submissions || []}
          onHistorySubmissionSelect={onHistorySubmissionSelect}
        />
      </Drawer>

      {isModalOpen && (
        <ConfirmDialog
          isOpen={isModalOpen}
          onClose={closeModal}
          onSubmit={submitModal}
          text="The contractor is deactivated. Please, make sure you assign the worklog to an active contractor."
          cancelBtn="Cancel"
          confirmBtn="Ok"
        />
      )}
      {isDeleteModalOpen && (
        <ConfirmDialog
          isOpen={isDeleteModalOpen}
          onClose={closeModal}
          onSubmit={handleDeleteSheet}
          text="Are you sure you want to remove this project from the schedule?"
          loadingOnSubmit
        />
      )}
      {isCancelModalOpen && (
        <CancelModal isOpen={isCancelModalOpen} onClose={closeModal} onSubmit={cancelSheet} />
      )}
      {overlappingModal && (
        <OverlappingModal
          data={overlappingModal.data || []}
          message={overlappingModal.reason || ''}
          onClose={closeOverlappingModal}
          onSave={handleFailedUncancel}
        />
      )}
      {userWorklogsModal && (
        <ChangeTimeModal
          data={userWorklogsModal}
          onClose={closeUserWorklogsModal}
          save={closeUserWorklogsModal}
        />
      )}
      {/* {equipWorklogsModal && (
        <ChangeEquipTimeModal
          data={userWorklogsModal}
          onClose={closeUserWorklogsModal}
          save={closeUserWorklogsModal}
        />
      )} */}
      {infoModal.isOpen && (
        <AlertModal isOpen={infoModal.isOpen} info={infoModal.text} onClose={closeInfoModal} />
      )}
      {confirmModal.isOpen && confirmModal.type === 'resources' && (
        <ConfirmDialog
          isOpen={confirmModal.isOpen}
          onClose={closeModal}
          onSubmit={clearAllResources}
          text={confirmModal.text}
        />
      )}

      <SubmittedConfirmModal />
    </>
  );
};

export default WorklogDetails;
